import { MAX_BINDINGS_BY_ZONE } from './workoutFormConstants'
import { ZONE_META } from './workoutCollectionValidation'
import { ensureBindingRowUid } from './workoutBindingRowUid'
import {
  ACTION_ADAPTATION_FIELD_KEYS,
  DEFAULT_ACTION_REST_SECONDS,
  isRepeatCountMissing,
  normalizeActionAdaptationFromExta,
  normalizeActionBindingsList,
  normalizeMusicLoopForRow,
  parseRepeatCount,
  parseRestSeconds,
  preservePersistedDurationIfNoRefMeta,
  readVideoDurationFromBindingRow,
  recomputeActionBindingRow,
  shouldClearRestForAction,
} from './workoutActionBindingCalculator'

/** 与后端 WorkoutCollectionBindingValidator 各区上限一致 */
export const MAX_COLLECTION_BINDINGS = MAX_BINDINGS_BY_ZONE.actions

export { createBindingRowUid, ensureBindingRowUid } from './workoutBindingRowUid'

export const BINDING_ZONE_UI = {
  actions: {
    badge: 'A',
    description: '按训练顺序绑定动作，配置循环次数与休息时长；单动作播放总长自动计算。',
    pickerTitle: '挑选训练动作',
    pickLabel: '挑选训练动作',
    pickHint: '从已发布的 workout_action 资源中选择',
    continueLabel: '继续挑选动作',
  },
  music: {
    badge: 'M',
    description: '绑定背景音乐；loop 须在音乐资源 loopable=true 时方可设为 true。',
    pickerTitle: '挑选背景音乐',
    pickLabel: '挑选背景音乐',
    pickHint: '从已发布的 workout_music 资源中选择',
    continueLabel: '继续挑选音乐',
  },
  voices: {
    badge: 'V',
    description: '绑定教练语音引导资源，顺序即播放顺序。',
    pickerTitle: '挑选语音引导',
    pickLabel: '挑选语音引导',
    pickHint: '从已发布的 workout_voice 资源中选择',
    continueLabel: '继续挑选语音',
  },
}

/** 三区 UI 元数据：label/assetType 来自 ZONE_META，文案来自 BINDING_ZONE_UI */
export const BINDING_ZONES = Object.keys(ZONE_META).map((id) => ({
  id,
  title: ZONE_META[id].label,
  assetType: ZONE_META[id].assetType,
  maxBindings: MAX_BINDINGS_BY_ZONE[id],
  ...BINDING_ZONE_UI[id],
}))

/** V 缺失时保留已落库的 video_duration_seconds，避免编辑态把时长冲掉。 */
function preservePersistedAdaptationFields(normalized, source) {
  const persisted = {}
  ACTION_ADAPTATION_FIELD_KEYS.forEach((key) => {
    if (source[key] != null && source[key] !== '') {
      persisted[key] = source[key]
    }
  })
  return { ...normalized, ...persisted }
}

function mapActionScalars(source) {
  const base = normalizeActionAdaptationFromExta(source || {})
  return {
    resource_id: base.resource_id || '',
    resource_name: base.resource_name || '',
    resource_db_id: base.resource_db_id ?? null,
    ref_meta: base.ref_meta ?? null,
    duration_seconds: base.duration_seconds,
    repeat_count: isRepeatCountMissing(base) ? null : parseRepeatCount(base.repeat_count, 0),
    rest_seconds: parseRestSeconds(base.rest_seconds, 0),
    video_duration_seconds: base.video_duration_seconds,
  }
}

export function emptyMusicRow() {
  return {
    resource_id: '',
    resource_name: '',
    resource_db_id: null,
    loop: false,
    ref_meta: null,
  }
}

export function emptyVoiceRow() {
  return {
    resource_id: '',
    resource_name: '',
    resource_db_id: null,
    ref_meta: null,
  }
}

export function emptyCollectionForm() {
  return {
    display_name: '',
    subtitle: '',
    difficulty: '',
    description: '',
    actions: [],
    music: [],
    voices: [],
  }
}

export function normalizeActionRow(row, rowIndex = 0, totalRows = 1) {
  const source = row || {}
  const normalized = mapActionScalars(source)

  if (readVideoDurationFromBindingRow(normalized)) {
    const recomputed = recomputeActionBindingRow(normalized, { index: rowIndex, totalRows })
    // 落库瘦身行无说明书时保留已落库 duration，与列表 normalize / 拖拽同一套护栏
    return ensureBindingRowUid(preservePersistedDurationIfNoRefMeta(source, recomputed))
  }

  return ensureBindingRowUid(
    preservePersistedAdaptationFields(
      {
        ...normalized,
        repeat_count: parseRepeatCount(normalized.repeat_count, 0),
        rest_seconds:
          shouldClearRestForAction(rowIndex, totalRows) ? 0 : parseRestSeconds(normalized.rest_seconds, 0),
        duration_seconds: 0,
      },
      source
    )
  )
}

/** 兼容历史 exta 把 loop 存成字符串；Boolean('false')===true 会误伤。 */
function parseLooseBoolean(value, fallback = false) {
  if (typeof value === 'boolean') {
    return value
  }
  if (value === 1 || value === '1' || value === 'true' || value === 'TRUE') {
    return true
  }
  if (value === 0 || value === '0' || value === 'false' || value === 'FALSE' || value == null || value === '') {
    return false
  }
  return fallback
}

export function normalizeMusicRow(row) {
  const mapped = normalizeMusicLoopForRow({ ...emptyMusicRow(), ...row })
  if (typeof mapped.loop !== 'boolean') {
    mapped.loop = parseLooseBoolean(mapped.loop, false)
  }
  return ensureBindingRowUid(mapped)
}

export function normalizeVoiceRow(row) {
  return ensureBindingRowUid({ ...emptyVoiceRow(), ...row })
}

/**
 * 从 exta 还原合集三区 binding 编辑态（含 ref_meta 等 UI 字段，保存前由 sanitize 剔除）。
 */
export function loadCollectionBindingsFromExta(exta) {
  const data = exta || {}
  const rawActions = Array.isArray(data.actions) ? data.actions : []
  return {
    actions: rawActions.map((row, index) => normalizeActionRow(row, index, rawActions.length)),
    music: Array.isArray(data.music) ? data.music.map((row) => normalizeMusicRow(row)) : [],
    voices: Array.isArray(data.voices) ? data.voices.map((row) => normalizeVoiceRow(row)) : [],
  }
}

/** Picker 选中行：repeat 从 0 开始，rest 默认 10s（列表 normalize 时末条仍清零）。 */
export function mapPickedRow(zoneId, pickerRow) {
  const base = {
    resource_db_id: pickerRow.id || null,
    resource_id: pickerRow.key,
    resource_name: pickerRow.resourceName || pickerRow.key,
  }
  if (zoneId === 'actions') {
    return ensureBindingRowUid(
      mapActionScalars({
        ...base,
        repeat_count: 0,
        rest_seconds: DEFAULT_ACTION_REST_SECONDS,
      })
    )
  }
  if (zoneId === 'music') {
    return ensureBindingRowUid({ ...emptyMusicRow(), ...base })
  }
  return ensureBindingRowUid({ ...emptyVoiceRow(), ...base })
}

export function findBindingZone(zoneId) {
  return BINDING_ZONES.find((zone) => zone.id === zoneId) || BINDING_ZONES[0]
}
